import {Component, Inject, OnInit, Optional} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {DialogService, DIALOG_DATA} from 'src/app/services/dialog.service';
import {DialogRef} from 'src/app/shared/ui-kits/dialog/dialog-ref';
import {LinkDocModalComponent} from '../link-doc-modal/link-doc-modal.component';
import {SureModalComponent} from '../sure-modal/sure-modal.component';
import {
  columnTypes,
  ITableModalData,
  ITableValidation,
  ProfessionalTableType,
  UITableHeader,
} from '../table.models';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {IDropdownObject} from 'src/app/interfaces/dropdown-object.interface';
import {Utils} from 'src/app/shared/utils/utils';
import {filter} from 'rxjs';
import {SureModalService} from 'src/app/services/sure-modal/sure-modal.service';
import {SURE_MODAL_TEXT} from 'src/app/shared/utils/constants';

@UntilDestroy()
@Component({
  selector: 'app-table-modal',
  templateUrl: './table-modal.component.html',
  styleUrls: ['./table-modal.component.scss'],
})
export class TableModalComponent implements OnInit {
  public ProfessionalTableType = ProfessionalTableType;
  public isEditable = false; // changes read/edit mode
  public isCreate = false; // shoes add mode
  public modalLink!: UntypedFormGroup; // modal group control
  _data!: any;
  typeOfData = columnTypes;
  editModalGroup!: UntypedFormGroup;
  public defaultDropdown: IDropdownObject[] = [];
  public trainingHeaders = [
    'conferenceName',
    'courseName',
    'presentationName',
    'name',
  ];
  public isAbleToDelete: boolean | undefined = false;
  public isEqualObjects = Utils.isEqualObjects;
  public isFieldInvalid = Utils.isFieldInvalid;
  public deadLine: null | Date = null;
  public addictedControlName = '';
  public isDatePickerOpened!: boolean;

  constructor(
    private dialogService: DialogService,
    private dialogRef: DialogRef,
    private sureModalService: SureModalService,
    @Optional() @Inject(DIALOG_DATA) public data: ITableModalData
  ) {}

  ngOnInit(): void {
    this.setTrainingDropdown(this.data.table);
    this.isAbleToDelete = this.data?.canDelete;
    this._data = {...this.data.content};
    this.editModalGroup = new UntypedFormGroup({});
    this.data.headers.forEach((element: UITableHeader) => {
      this.editModalGroup.addControl(
        element.key,
        new UntypedFormControl(
          element.type === columnTypes.dropDown && !this._data[element.key]
            ? null
            : this._data[`${element.key}`],
          this.data.validation?.find(
            (v: ITableValidation) => v.controlName === element.key
          )?.validators
        )
      );
    });
    this.editModalGroup.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this._data = res;
      });
    if (this.data.table === ProfessionalTableType.EnglishCourses) {
      const course = this.data.dropdown.EnglishCourses.find(
        (e: IDropdownObject) => e.name === this.data.content.name
      );
      this.editModalGroup.get('name')?.setValue(course);
    }
    this.setDateRestrictions(this.data.table);
  }

  changes() {
    if (this.isEqualObjects(this._data, this.data.content)) {
      this.dialogRef.close();
    } else {
      this.sureModalService
        .openModal({text: SURE_MODAL_TEXT.CHANGES_TEXT})
        .subscribe(result => {
          this.dialogRef.close(result ? this._data : '');
        });
    }
  }

  saveChanges() {
    if (this.editModalGroup.invalid) {
      return Utils.validateAllFormFields(this.editModalGroup);
    }
    this._data.link = this.editModalGroup.getRawValue().link;
    if (this.isEqualObjects(this._data, this.data.content)) {
      this.dialogRef.close();
    } else {
      Object.keys(this._data).forEach(element => {
        if (this.trainingHeaders.includes(element)) {
          this._data['training'] = this._data[element];
        }
      });
      this.dialogRef.close(this._data);
    }
  }

  setTrainingDropdown(tableName: ProfessionalTableType) {
    switch (tableName) {
      case ProfessionalTableType.Conference:
        this.defaultDropdown = this.data.dropdown.Conference;
        break;
      case ProfessionalTableType.InternalCourses:
        this.defaultDropdown = this.data.dropdown.InternalCourses;
        break;
      case ProfessionalTableType.Presentation:
        this.defaultDropdown = this.data.dropdown.Presentation;
        break;
      case ProfessionalTableType.Training:
        this.defaultDropdown = this.data.dropdown.Training;
        break;
      case ProfessionalTableType.TechInterview:
        this.defaultDropdown = this.data.dropdown.InterviewStatus;
        break;
    }
  }

  /**
   * Method open modall window to attach link
   */
  attachLink(): void {
    this.dialogService
      .open(LinkDocModalComponent, {
        data: null,
        settings: {
          width: '350px',
          height: '250px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(res => !!res)
      )
      .subscribe(result => {
        this.editModalGroup.get('file')?.setValue(result);
      });
  }

  /**
   * This method removes document link from link control
   */
  deleteDocLink(): void {
    this.editModalGroup.controls['file'].setValue('');
  }

  deleteRow() {
    this.dialogRef.close('delete');
  }

  setDateRestrictions(tableName: ProfessionalTableType): void {
    let controlName = '';
    switch (tableName) {
      case ProfessionalTableType.Certifications:
        controlName = 'date';
        this.addictedControlName = 'expirationDate';
        break;
      case ProfessionalTableType.AssignedMentors:
        controlName = 'start';
        this.addictedControlName = 'endDate';
        break;
    }
    if (controlName) {
      this.editModalGroup.controls[controlName].valueChanges
        .pipe(
          untilDestroyed(this),
          filter(data => !!data)
        )
        .subscribe(value => {
          this.deadLine = Utils.formatStringToDate(value);
        });
    }
  }
}
